import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdMoreVert } from 'react-icons/md';
import { Button } from '@/components/Button';
import { Tag } from '@/components/Tag';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { renderTextWithLinks } from '@/utils/textWithLinks';
import type {
  AgentApiCandidateChatCommentsRecord,
  AgentApiCandidateInterviewLogsRecord,
  SharedCustomField,
} from '@/types/serializers';

type Props = {
  chatComment: AgentApiCandidateChatCommentsRecord;
  customFields: SharedCustomField[];
  canDelete: boolean;
  onEditInterviewLog: (interviewLogId: number) => void;
  onDelete: (chatComment: AgentApiCandidateChatCommentsRecord) => void;
};

const formatPostedAt = (postedAt: string) => postedAt.replace(/-/g, '/');

const formatInterviewedAt = (interviewedAt: string) => {
  const [date, time] = interviewedAt.split(' ');
  const [year, month, day] = date.split('-').map(Number);
  return `${year}年${month}月${day}日${time ? ` ${time}` : ''}`;
};

function InterviewLogText({
  interviewLog,
  customFields,
}: {
  interviewLog: AgentApiCandidateInterviewLogsRecord;
  customFields: SharedCustomField[];
}) {
  const items = customFields.flatMap((field) => {
    const datum = interviewLog.custom_data.find((d) => d.custom_field.id === field.id);
    return datum?.content ? [{ label: field.name, content: datum.content }] : [];
  });

  return (
    <>
      <span className="font-bold">■面談日時</span>
      {`\n${formatInterviewedAt(interviewLog.interviewed_at)}`}
      {items.map((item) => (
        <Fragment key={item.label}>
          {'\n'}
          <span className="font-bold">■{item.label}</span>
          {'\n'}
          {renderTextWithLinks(item.content)}
        </Fragment>
      ))}
    </>
  );
}

export default function CandidatesTimelineItem({
  chatComment,
  customFields,
  canDelete,
  onEditInterviewLog,
  onDelete,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const bodyRef = useRef<HTMLParagraphElement>(null);

  const interviewLog = chatComment.interview_log;
  const author = interviewLog ? interviewLog.organization_user : chatComment.author;
  const avatarUrl =
    chatComment.source_type === 'user' || interviewLog
      ? author?.avatar_url || '/agent/default-avatar.png'
      : '/agent/candidates/icon-chat-system.svg';
  const isDeletable = !!interviewLog || canDelete;

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      if (el.classList.contains('line-clamp-5'))
        setIsOverflowing(el.scrollHeight > el.clientHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex items-start gap-sm rounded-sm bg-white p-md shadow-layer1">
      <img
        src={avatarUrl}
        width={42}
        height={42}
        alt=""
        className="size-[42px] shrink-0 rounded-full object-cover"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-xs">
        <div className="flex items-center justify-between gap-xs">
          <div className="flex min-w-0 items-center gap-xs">
            {author && <p className="truncate text-heading-xxs text-main">{author.name}</p>}
            {interviewLog && (
              <Tag color="orange" size="sm" className="shrink-0">
                面談
              </Tag>
            )}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-xs">
            <p className="text-body-xs text-placeholder">{formatPostedAt(chatComment.posted_at)}</p>
            {isDeletable && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button type="button" color="secondary" size="sm" iconOnly aria-label="メニュー">
                    <MdMoreVert className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-auto min-w-0 rounded-sm border-0 p-0 shadow-layer2"
                >
                  {interviewLog && (
                    <DropdownMenuItem
                      className="h-xxl cursor-pointer rounded-none px-sm text-body-sm"
                      onSelect={() => onEditInterviewLog(interviewLog.id)}
                    >
                      編集
                    </DropdownMenuItem>
                  )}
                  {isDeletable && (
                    <DropdownMenuItem
                      className="h-xxl cursor-pointer rounded-none px-sm text-body-sm text-danger focus:text-danger"
                      onSelect={() => onDelete(chatComment)}
                    >
                      削除
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <div className="flex flex-col items-start gap-xxs">
          <p
            ref={bodyRef}
            className={clsx(
              'w-full text-body-xs whitespace-pre-wrap text-main',
              !isExpanded && 'line-clamp-5'
            )}
          >
            {interviewLog ? (
              <InterviewLogText interviewLog={interviewLog} customFields={customFields} />
            ) : (
              chatComment.content.map((c, index) => (
                <span key={index}>{renderTextWithLinks(c.text)}</span>
              ))
            )}
          </p>
          {isOverflowing && (
            <Button
              type="button"
              color="tertiary"
              size="sm"
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded ? 'とじる' : 'もっと見る'}
              {isExpanded ? (
                <MdKeyboardArrowUp className="size-3" />
              ) : (
                <MdKeyboardArrowDown className="size-3" />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
